import type { NotificationType } from '@univdiam/ui/type'
import { useCallback, useEffect, useState, useRef } from 'react'
import NotifCard from './NotifCard'
import { Card, CardContent } from '@univdiam/ui/components/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@univdiam/ui/components/select'
import { Input } from '@univdiam/ui/components/input'
import { Button } from '@univdiam/ui/components/button'
import { Search, ChevronLeft, ChevronRight, X } from 'lucide-react'

const ALL_VALUE = 'all'

const READ_STATUS_OPTIONS = [
  { value: 'unread', label: 'Unread' },
  { value: 'read', label: 'Read' },
  { value: ALL_VALUE, label: 'All' },
]

const ACTION_TYPE_OPTIONS = [
  { value: ALL_VALUE, label: 'All Types' },
  { value: 'status_updated', label: 'Status Change' },
  { value: 'chat_message', label: 'Message' },
  { value: 'additional_instruction_added', label: 'Instruction Added' },
  { value: 'additional_instruction_updated', label: 'Instruction Updated' },
  { value: 'additional_instruction_deleted', label: 'Instruction Deleted' },
  { value: 'cad_added', label: 'CAD Added' },
  { value: 'order_created', label: 'Order Created' },
  { value: 'cad_order_created', label: 'CAD Order Created' },
]

export type NotificationFilters = {
  order_id: string
  action_type: string
  search: string
  start_date: string
  end_date: string
  is_seen: string
  page: number
}

export type PaginationInfo = {
  total: number
  page: number
  totalPages: number
  limit: number
}

export type OrderRefOption = {
  order_ref: string
  order_internal_id: string
  job_title: string
}

interface GroupedNotifications {
  orderId: string
  notifications: NotificationType[]
  latestTime: number
}

const Notifications = ({
  isAdmin,
  data,
  onMarkAsSeen,
  filters,
  onFilterChange,
  orderOptions = [],
  pagination,
  onPageChange,
  fetchNextOrderRefs,
  hasNextOrderRefs,
  isFetchingNextOrderRefs
}: {
  isAdmin?: boolean,
  data: NotificationType[],
  onMarkAsSeen?: (id: string | number) => void,
  filters?: NotificationFilters,
  onFilterChange?: (filters: Partial<NotificationFilters>) => void,
  orderOptions?: OrderRefOption[],
  pagination?: PaginationInfo,
  onPageChange?: (page: number) => void,
  fetchNextOrderRefs?: () => void,
  hasNextOrderRefs?: boolean,
  isFetchingNextOrderRefs?: boolean
}) => {
  const [groupedDataByOrder, setGroupedDataByOrder] = useState<GroupedNotifications[]>([])
  const [searchInput, setSearchInput] = useState(filters?.search || '')
  
  // The sentinel lives inside the order dropdown, so it only exists while the
  // dropdown is open. A useEffect ran once with no node and never re-ran on
  // open, so scrolling never loaded more orders. A callback ref runs whenever
  // the node mounts or unmounts.
  const observerRef = useRef<IntersectionObserver | null>(null)
  const observerTarget = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect()
    observerRef.current = null
    if (!node || !fetchNextOrderRefs || !hasNextOrderRefs) return
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextOrderRefs) fetchNextOrderRefs()
      },
      { threshold: 0.1 }
    )
    observerRef.current.observe(node)
  }, [fetchNextOrderRefs, hasNextOrderRefs, isFetchingNextOrderRefs])

  useEffect(() => {
    if (data?.length > 0) {
      // Sort all data by created_at (latest first)
      const sortedData = [...data].sort(
        (a, b) =>
          new Date(b.notification_created_at!).getTime() -
          new Date(a.notification_created_at!).getTime()
      )

      const grouped: Record<string, NotificationType[]> = {}

      sortedData.forEach(n => {
        const orderId = n.actual_order_id || `#${n.order_id}`
        if (!grouped[orderId]) grouped[orderId] = []
        grouped[orderId].push(n)
      })

      // Convert to array & sort groups by latest notification time
      const groups: GroupedNotifications[] = Object.entries(grouped).map(
        ([orderId, notifications]) => ({
          orderId,
          notifications,
          latestTime: new Date(notifications[0]?.notification_created_at!).getTime()
        })
      )

      groups.sort((a, b) => b.latestTime - a.latestTime)

      setGroupedDataByOrder(groups)
    } else {
      setGroupedDataByOrder([])
    }
  }, [data])

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onFilterChange?.({ search: searchInput, page: 1 })
  }

  const clearFilters = () => {
    setSearchInput('')
    onFilterChange?.({
      order_id: '',
      action_type: '',
      search: '',
      start_date: '',
      end_date: '',
      is_seen: 'unread',
      page: 1
    })
  }

  const hasActiveFilters = filters?.order_id || filters?.action_type || filters?.search || filters?.start_date || filters?.end_date

  return (
    <div className="w-full max-w-[1200px] mx-auto p-4 md:p-[24px]">
      <div className="flex items-center justify-between mb-4 md:mb-[24px]">
        <p className="text-xl md:text-2xl font-semibold">Notifications</p>
        {pagination && (
          <p className="text-sm text-gray-500">
            {pagination.total} {pagination.total === 1 ? 'notification' : 'notifications'}
          </p>
        )}
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-[#E9EAEB] rounded-md p-3 md:p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-3 flex-wrap">
          {/* Read Status Filter */}
          <div className="w-full md:w-[130px]">
            <Select
              value={!filters?.is_seen || filters.is_seen === '' ? ALL_VALUE : filters.is_seen}
              onValueChange={(val) => onFilterChange?.({ is_seen: val === ALL_VALUE ? '' : val, page: 1 })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Unread" />
              </SelectTrigger>
              <SelectContent>
                {READ_STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Order Filter */}
          <div className="w-full md:w-[200px]">
            <Select
              value={filters?.order_id || ALL_VALUE}
              onValueChange={(val) => onFilterChange?.({ order_id: val === ALL_VALUE ? '' : val, page: 1 })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="All Orders" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>All Orders</SelectItem>
                {orderOptions.map((opt) => (
                  <SelectItem key={opt.order_ref} value={opt.order_ref}>
                    {opt.order_ref} {opt.job_title ? `- ${opt.job_title}` : ''}
                  </SelectItem>
                ))}
                {hasNextOrderRefs && (
                  <div ref={observerTarget} className="py-2 text-center text-xs text-muted-foreground w-full">
                    {isFetchingNextOrderRefs ? 'Loading more...' : 'Load more'}
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Action Type Filter */}
          <div className="w-full md:w-[180px]">
            <Select
              value={filters?.action_type || ALL_VALUE}
              onValueChange={(val) => onFilterChange?.({ action_type: val === ALL_VALUE ? '' : val, page: 1 })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                {ACTION_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date Range */}
          <div className="flex gap-2 items-center">
            <Input
              type="date"
              value={filters?.start_date?.split('T')[0] || ''}
              onChange={(e) => onFilterChange?.({ start_date: e.target.value, page: 1 })}
              max={filters?.end_date?.split('T')[0] || undefined}
              className="w-[140px]"
              placeholder="From"
            />
            <span className="text-gray-400">—</span>
            <Input
              type="date"
              value={filters?.end_date?.split('T')[0] || ''}
              onChange={(e) => onFilterChange?.({ end_date: e.target.value, page: 1 })}
              min={filters?.start_date?.split('T')[0] || undefined}
              className="w-[140px]"
              placeholder="To"
            />
          </div>

          {/* Search */}
          <form onSubmit={handleSearchSubmit} className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by order ID or title..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-8 w-full"
              />
            </div>
          </form>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="shrink-0"
            >
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      {groupedDataByOrder.map(group => (
        <div key={group.orderId} className="mb-4">
          <h2 className="text-lg mb-2 text-[#555555]">
            Order ID: {group.orderId}
          </h2>
          <Card className="overflow-hidden">
            <CardContent className="p-0 flex flex-col">
              {group.notifications.map((notification, idx) => (
                <NotifCard
                  key={notification.id}
                  data={notification}
                  isAdmin={isAdmin}
                  onMarkAsSeen={onMarkAsSeen}
                  isLast={idx === group.notifications.length - 1}
                />
              ))}
            </CardContent>
          </Card>
        </div>
      ))}

      {groupedDataByOrder.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p>No notifications found</p>
          {hasActiveFilters && (
            <Button variant="link" onClick={clearFilters} className="mt-2">
              Clear filters to see all notifications
            </Button>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page <= 1}
            onClick={() => onPageChange?.(pagination.page - 1)}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Prev
          </Button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
              let pageNum: number
              if (pagination.totalPages <= 5) {
                pageNum = i + 1
              } else if (pagination.page <= 3) {
                pageNum = i + 1
              } else if (pagination.page >= pagination.totalPages - 2) {
                pageNum = pagination.totalPages - 4 + i
              } else {
                pageNum = pagination.page - 2 + i
              }
              return (
                <Button
                  key={pageNum}
                  variant={pagination.page === pageNum ? 'default' : 'outline'}
                  size="sm"
                  className="w-8 h-8 p-0"
                  onClick={() => onPageChange?.(pageNum)}
                >
                  {pageNum}
                </Button>
              )
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => onPageChange?.(pagination.page + 1)}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  )
}

export default Notifications
